// ════════════════════════════════════════════════════════════
// Enrolar2FA — pantalla obligatoria de configuración del 2FA
// ════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Alert, CircularProgress
} from '@mui/material';
import { iniciar2FA, confirmar2FA } from '../api';

export default function Enrolar2FA({ onCompletado }: { onCompletado: () => void }) {
  const [qr, setQr] = useState('');
  const [secreto, setSecreto] = useState('');
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    iniciar2FA().then((d: any) => { setQr(d.qr); setSecreto(d.secreto); })
      .catch(() => setError('Error al generar el código QR. Recarga la página.'));
  }, []);

  const confirmar = async () => {
    setCargando(true);
    setError('');
    try {
      await confirmar2FA(codigo.trim());
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      user.totp_activo = true;
      localStorage.setItem('user', JSON.stringify(user));
      onCompletado();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Código incorrecto');
      setCodigo('');
    } finally {
      setCargando(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: '#0D0D0D', backgroundImage: 'radial-gradient(circle at 50% 0%, #1a1a1a 0%, #0D0D0D 70%)',
      px: 2, py: 4
    }}>
      <Card sx={{ width: '100%', maxWidth: 440, borderRadius: 3, borderTop: '3px solid #F5C200' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>🔐 Configura la verificación en dos pasos</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Por seguridad, todos los usuarios deben proteger su cuenta con un código
            del móvil. Solo tendrás que hacerlo una vez.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {!qr ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                <b>1.</b> Instala <b>Microsoft Authenticator</b> en tu móvil (o cualquier
                app de códigos) → <b>+</b> → Otra cuenta → Escanear código QR.
              </Typography>
              <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                <img src={qr} alt="Código QR" style={{ width: 210, borderRadius: 8, background: 'white', padding: 8 }} />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2.5, textAlign: 'center' }}>
                Si no puedes escanear, introduce esta clave: <b>{secreto}</b>
              </Typography>

              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>2.</b> Escribe el código de 6 dígitos que muestra la app:
              </Typography>
              <TextField
                fullWidth value={codigo}
                onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={(e) => { if (e.key === 'Enter' && codigo.length === 6) confirmar(); }}
                inputProps={{ inputMode: 'numeric', style: { letterSpacing: 8, textAlign: 'center', fontWeight: 700, fontSize: 22 } }}
              />
              <Button fullWidth variant="contained" onClick={confirmar}
                disabled={cargando || codigo.length !== 6}
                sx={{ mt: 2, bgcolor: '#F5C200', color: '#0D0D0D', fontWeight: 700, py: 1.5,
                  '&:hover': { bgcolor: '#d9ab00' } }}>
                {cargando ? <CircularProgress size={24} color="inherit" /> : 'Activar y continuar'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}